import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Card } from "./ui/card";
import { Heart, ShieldCheck, TrendingUp, Users } from "lucide-react";
import { CountUp } from "./CountUp";
import api from "../lib/api";

/**
 * ImpactTracker — 4 headline metrics with live polling.
 *   - Facts verified (all-time)
 *   - Raised for Hands of Hope ($)
 *   - Donors count
 *   - Truth-rate progress bar toward the fundraising goal
 */
export default function ImpactTracker() {
    const [truth, setTruth] = useState(null);
    const [impact, setImpact] = useState(null);

    useEffect(() => {
        let alive = true;
        const load = () => {
            api.get("/stats/truth-score").then((r) => alive && setTruth(r.data)).catch(() => {});
            api.get("/impact/counter").then((r) => alive && setImpact(r.data)).catch(() => {});
        };
        load();
        const t = setInterval(load, 20_000);
        return () => { alive = false; clearInterval(t); };
    }, []);

    const raised = impact?.raised_usd || 0;
    const goal = impact?.goal_usd || 100_000;
    const donors = impact?.donations_count || 0;
    const factsVerified = truth?.total_all_time || 0;
    const raisedPct = Math.min(100, (raised / (goal || 1)) * 100);

    return (
        <section
            className="relative max-w-6xl mx-auto px-6 lg:px-10 py-20"
            data-testid="impact-tracker"
            aria-labelledby="impact-tracker-heading"
        >
            {/* ambient teal glow */}
            <div className="pointer-events-none absolute inset-x-0 top-1/2 -translate-y-1/2 h-72 bg-primary/8 blur-[100px] -z-0" />

            <div className="relative z-10 text-center mb-12">
                <div className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/15 px-3 py-1 text-[11px] font-semibold text-accent mb-4">
                    <TrendingUp className="h-3 w-3 text-primary animate-pulse" /> Live impact tracker
                </div>
                <h2 id="impact-tracker-heading" className="font-display text-3xl md:text-5xl text-accent tracking-tight leading-[1.08]">
                    Every truth you verify <span className="text-primary">funds hope</span>.
                </h2>
                <p className="mt-4 text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                    Real-Check runs on donations and Pro subscriptions.
                    Half of every dollar goes to <strong className="text-emerald-400">Hands of Hope</strong> — the rest keeps this platform independent and ad-free.
                </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 relative z-10">
                <MetricCard
                    icon={<ShieldCheck className="h-6 w-6 text-primary" />}
                    label="Facts verified"
                    value={factsVerified}
                    testid="impact-metric-facts"
                    tint="primary"
                />
                <MetricCard
                    icon={<Heart className="h-6 w-6 text-emerald-400" />}
                    label="Raised for Hands of Hope"
                    value={raised}
                    prefix="$"
                    decimals={0}
                    testid="impact-metric-raised"
                    tint="emerald"
                />
                <MetricCard
                    icon={<Users className="h-6 w-6 text-amber-300" />}
                    label="Compassionate donors"
                    value={donors}
                    testid="impact-metric-donors"
                    tint="amber"
                />
                <MetricCard
                    icon={<TrendingUp className="h-6 w-6 text-fuchsia-300" />}
                    label="Verdicts today"
                    value={truth?.total_today || 0}
                    testid="impact-metric-today"
                    tint="fuchsia"
                    accent
                />
            </div>

            {/* Fundraising progress bar */}
            <Card className="card-surface rounded-2xl p-6 md:p-7 mt-6 relative z-10 overflow-hidden" data-testid="impact-progress-card">
                <div className="absolute inset-0 bg-gradient-to-r from-emerald-500/10 via-transparent to-primary/10 pointer-events-none" />
                <div className="relative">
                    <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
                        <h3 className="font-display text-lg md:text-xl text-accent">Mission goal — Hands of Hope</h3>
                        <div className="text-sm text-muted-foreground">
                            <span className="text-2xl font-display font-bold text-emerald-400" data-testid="impact-raised-count">
                                $<CountUp value={raised} decimals={0} />
                            </span>
                            <span className="mx-2 opacity-50">/</span>
                            <span className="text-accent">${goal.toLocaleString()}</span>
                        </div>
                    </div>
                    <div className="h-3 w-full rounded-full bg-white/5 overflow-hidden border border-white/10">
                        <motion.div
                            className="h-full bg-gradient-to-r from-emerald-400 via-primary to-emerald-400"
                            style={{ boxShadow: "0 0 24px rgba(34,224,180,0.55)" }}
                            initial={{ width: 0 }}
                            animate={{ width: `${raisedPct}%` }}
                            transition={{ duration: 1.2, ease: "easeOut" }}
                            data-testid="impact-progress-bar"
                        />
                    </div>
                    <p className="text-xs text-muted-foreground mt-3">
                        Every donation and pre-order splits <strong className="text-accent">50/50</strong> — half to the charity, half to keep Real-Check independent.
                    </p>
                </div>
            </Card>
        </section>
    );
}

function MetricCard({ icon, label, value, testid, tint, prefix = "", decimals = 0, accent }) {
    const glowMap = {
        primary: "bg-primary/15",
        emerald: "bg-emerald-400/15",
        amber:   "bg-amber-300/15",
        fuchsia: "bg-fuchsia-300/15",
    };
    return (
        <Card
            className={`card-surface rounded-2xl p-6 relative overflow-hidden ${accent ? "border border-primary/25" : ""}`}
            data-testid={testid}
        >
            <div className={`absolute -top-10 -right-10 w-28 h-28 rounded-full blur-2xl ${glowMap[tint] || "bg-primary/15"} pointer-events-none`} />
            <div className="flex items-center gap-2 mb-4 relative">
                {icon}
                <p className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</p>
            </div>
            <div className="font-display text-4xl md:text-5xl font-bold text-accent tabular-nums leading-none relative">
                {prefix}
                <CountUp value={value} decimals={decimals} />
            </div>
        </Card>
    );
}
